import {
  BlockReasonSchema,
  type BlockReason,
  type Installment,
  type Statement,
} from '@paguz/contracts';
import {
  Badge,
  Button,
  cn,
  EmptyState,
  MoneyText,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast,
} from '@paguz/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import {
  ArrowLeftIcon,
  ChevronDownIcon,
  LockIcon,
  LockOpenIcon,
  ReceiptTextIcon,
} from 'lucide-react';
import { Fragment, useState } from 'react';
import type { AdminLineDetail } from '../../api/admin-schemas.ts';
import { api, queryKeys } from '../../api/endpoints.ts';
import { LINE_TABS, type LineTab } from '../../app/search.ts';
import { ConfirmActionDialog, SummaryRows } from '../../components/confirm-dialog.tsx';
import { Field, Select } from '../../components/form-controls.tsx';
import { CopyValue, ErrorState, LoadingRows, PageHeader, Panel } from '../../components/page.tsx';
import { formatBusinessDate, formatDateTime, formatRate } from '../../lib/format.ts';
import { BLOCK_REASON, DRAWDOWN_STATUS, INSTALLMENT_STATUS } from '../../lib/labels.ts';
import { useCan } from '../../lib/session.ts';
import { LineFigures, LineStatusBadge, UsageBar } from './line-summary.tsx';

const route = getRouteApi('/app/lineas/$lineId');

const TAB_LABEL: Readonly<Record<LineTab, string>> = {
  utilizaciones: 'Utilizaciones',
  cuotas: 'Cuotas',
  extractos: 'Extractos',
  pagos: 'Pagos',
  movimientos: 'Movimientos contables',
};

export function LinePage() {
  const { lineId } = route.useParams();
  const query = useQuery({ queryKey: queryKeys.line(lineId), queryFn: () => api.getLine(lineId) });
  return (
    <>
      {query.data && (
        <Link
          to="/clientes/$customerId"
          params={{ customerId: query.data.customer.id }}
          className="mb-3 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
        >
          <ArrowLeftIcon className="size-4" /> {query.data.customer.fullName}
        </Link>
      )}
      {query.isPending ? (
        <LoadingRows rows={8} />
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <LineDetail detail={query.data} />
      )}
    </>
  );
}

function LineDetail({ detail }: { detail: AdminLineDetail }) {
  const { line } = detail;
  const { tab = 'utilizaciones' } = route.useSearch();
  const navigate = route.useNavigate();
  const canBlock = useCan('lines:block');
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<'BLOCK' | 'UNBLOCK' | null>(null);
  const [blockReason, setBlockReason] = useState<BlockReason | ''>('');
  const tabs = LINE_TABS.filter((t) => t !== 'movimientos' || detail.movements !== null);

  const mutation = useMutation({
    mutationFn: (input: { action: 'BLOCK' | 'UNBLOCK'; reason: string }) =>
      api.setLineStatus(line.id, {
        action: input.action,
        reason: input.reason,
        ...(input.action === 'BLOCK' && blockReason !== '' && { blockReason }),
      }),
    onSuccess: (data, input) => {
      if (data) queryClient.setQueryData(queryKeys.line(line.id), data);
      else void queryClient.invalidateQueries({ queryKey: queryKeys.line(line.id) });
      void queryClient.invalidateQueries({ queryKey: ['customer'] });
      toast.success(input.action === 'BLOCK' ? 'Línea bloqueada' : 'Línea desbloqueada');
    },
  });

  return (
    <>
      <PageHeader
        eyebrow={<CopyValue value={line.id} label="ID de la línea" />}
        title={`Línea de ${detail.customer.fullName}`}
        description={`${line.productName} · v${line.productVersion} · ${formatRate(line.product.rateEA)} (${formatRate(line.product.rateMV, 'MV')})`}
        actions={
          <>
            <LineStatusBadge line={line} />
            {canBlock &&
              (line.status === 'BLOCKED' ? (
                <Button
                  variant="outline"
                  onClick={() => {
                    setDialog('UNBLOCK');
                  }}
                >
                  <LockOpenIcon /> Desbloquear
                </Button>
              ) : line.status !== 'CLOSED' ? (
                <Button
                  variant="outline"
                  onClick={() => {
                    setDialog('BLOCK');
                  }}
                >
                  <LockIcon /> Bloquear
                </Button>
              ) : null)}
          </>
        }
      />

      <Panel className="mb-6">
        <LineFigures line={line} />
        <div className="mt-5">
          <UsageBar line={line} />
        </div>
        {line.blockReason && (
          <p className="mt-4 text-sm text-destructive">
            Bloqueada por: {BLOCK_REASON[line.blockReason]}
          </p>
        )}
      </Panel>

      <Tabs
        value={tab}
        onValueChange={(value) => {
          void navigate({ search: { tab: value as LineTab } });
        }}
      >
        <TabsList variant="line">
          {tabs.map((t) => (
            <TabsTrigger key={t} value={t}>
              {TAB_LABEL[t]}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="utilizaciones">
          <Drawdowns detail={detail} />
        </TabsContent>
        <TabsContent value="cuotas">
          <InstallmentCalendar detail={detail} />
        </TabsContent>
        <TabsContent value="extractos">
          <Statements statements={detail.statements} />
        </TabsContent>
        <TabsContent value="pagos">
          <Payments detail={detail} />
        </TabsContent>
        {detail.movements && (
          <TabsContent value="movimientos">
            <Movements movements={detail.movements} />
          </TabsContent>
        )}
      </Tabs>

      <ConfirmActionDialog
        open={dialog !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDialog(null);
            setBlockReason('');
          }
        }}
        title={dialog === 'BLOCK' ? 'Bloquear la línea' : 'Desbloquear la línea'}
        description={
          dialog === 'BLOCK'
            ? 'El cliente no podrá comprar con su cupo hasta que se desbloquee. Pagos y extractos siguen normales.'
            : 'El cliente podrá volver a comprar (si no está en mora).'
        }
        destructive={dialog === 'BLOCK'}
        confirmLabel={dialog === 'BLOCK' ? 'Bloquear línea' : 'Desbloquear línea'}
        summary={
          <SummaryRows
            rows={[
              ['Cliente', detail.customer.fullName],
              ['Línea', line.id],
              ['Disponible', <MoneyText key="d" value={line.availableLimit.amount} size="sm" />],
            ]}
          />
        }
        extra={
          dialog === 'BLOCK' && (
            <Field label="Motivo codificado" htmlFor="block-reason">
              <Select
                id="block-reason"
                value={blockReason}
                onChange={(event) => {
                  setBlockReason(event.target.value as BlockReason | '');
                }}
              >
                <option value="">Elige un motivo</option>
                {BlockReasonSchema.options.map((r) => (
                  <option key={r} value={r}>
                    {BLOCK_REASON[r]}
                  </option>
                ))}
              </Select>
            </Field>
          )
        }
        extraValid={dialog !== 'BLOCK' || blockReason !== ''}
        onConfirm={async (reason) => {
          if (!dialog) return;
          await mutation.mutateAsync({ action: dialog, reason });
        }}
      />
    </>
  );
}

function Drawdowns({ detail }: { detail: AdminLineDetail }) {
  const [open, setOpen] = useState<string | null>(detail.drawdowns[0]?.id ?? null);
  if (detail.drawdowns.length === 0) {
    return (
      <EmptyState
        icon={<ReceiptTextIcon />}
        title="Sin utilizaciones"
        description="Las compras con cupo aparecerán aquí."
      />
    );
  }
  return (
    <Panel bodyClassName="p-0">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-10" />
            <TableHead>Fecha</TableHead>
            <TableHead>Comercio</TableHead>
            <TableHead className="text-right">Valor</TableHead>
            <TableHead className="text-right">Saldo</TableHead>
            <TableHead className="text-center">Cuotas</TableHead>
            <TableHead>Tasa</TableHead>
            <TableHead>Estado</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {detail.drawdowns.map((dd) => {
            const isOpen = open === dd.id;
            return (
              <Fragment key={dd.id}>
                <TableRow>
                  <TableCell>
                    <button
                      type="button"
                      aria-expanded={isOpen}
                      aria-label={`${isOpen ? 'Ocultar' : 'Ver'} plan de cuotas`}
                      className="inline-flex size-8 cursor-pointer items-center justify-center rounded-md hover:bg-accent"
                      onClick={() => {
                        setOpen(isOpen ? null : dd.id);
                      }}
                    >
                      <ChevronDownIcon
                        className={cn('size-4 transition-transform', isOpen && 'rotate-180')}
                      />
                    </button>
                  </TableCell>
                  <TableCell>{formatBusinessDate(dd.drawdownDate)}</TableCell>
                  <TableCell>
                    <div className="font-semibold">{dd.merchant.name}</div>
                    {dd.paymentIntentId && (
                      <Link
                        to="/transacciones/$paymentIntentId"
                        params={{ paymentIntentId: dd.paymentIntentId }}
                        className="text-xs text-primary hover:underline"
                      >
                        Ver transacción
                      </Link>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <MoneyText value={dd.principal.amount} size="sm" />
                  </TableCell>
                  <TableCell className="text-right">
                    <MoneyText value={dd.outstandingPrincipal.amount} size="sm" />
                  </TableCell>
                  <TableCell className="text-center tabular-nums">{dd.installments}</TableCell>
                  <TableCell>{formatRate(dd.rateEA)}</TableCell>
                  <TableCell>
                    <Badge variant={DRAWDOWN_STATUS[dd.status].tone}>
                      {DRAWDOWN_STATUS[dd.status].label}
                    </Badge>
                  </TableCell>
                </TableRow>
                {isOpen && (
                  <TableRow className="bg-muted/30 hover:bg-muted/30">
                    <TableCell />
                    <TableCell colSpan={7} className="py-3">
                      <ScheduleTable rows={dd.schedule} />
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </Panel>
  );
}

function ScheduleTable({ rows }: { rows: readonly Installment[] }) {
  return (
    <table className="w-full text-sm">
      <caption className="mb-2 caption-top text-left text-xs font-semibold text-muted-foreground">
        Plan de cuotas (Credit Core)
      </caption>
      <thead>
        <tr className="text-left text-xs text-muted-foreground">
          <th className="py-1 font-semibold">#</th>
          <th className="py-1 font-semibold">Vence</th>
          <th className="py-1 text-right font-semibold">Capital</th>
          <th className="py-1 text-right font-semibold">Interés</th>
          <th className="py-1 text-right font-semibold">Seguro</th>
          <th className="py-1 text-right font-semibold">Total</th>
          <th className="py-1 pl-4 font-semibold">Estado</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.number} className="border-t border-border/60">
            <td className="py-1.5 tabular-nums">{row.number}</td>
            <td className="py-1.5">{formatBusinessDate(row.dueDate)}</td>
            <td className="py-1.5 text-right">
              <MoneyText value={row.principal.amount} size="sm" />
            </td>
            <td className="py-1.5 text-right">
              <MoneyText value={row.interest.amount} size="sm" />
            </td>
            <td className="py-1.5 text-right">
              <MoneyText value={row.insurance.amount} size="sm" />
            </td>
            <td className="py-1.5 text-right font-semibold">
              <MoneyText value={row.total.amount} size="sm" />
            </td>
            <td className="py-1.5 pl-4">
              <Badge variant={INSTALLMENT_STATUS[row.status].tone}>
                {INSTALLMENT_STATUS[row.status].label}
              </Badge>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function InstallmentCalendar({ detail }: { detail: AdminLineDetail }) {
  const rows = detail.drawdowns
    .flatMap((dd) =>
      dd.schedule.map((row) => ({
        ...row,
        merchant: dd.merchant.name,
        of: dd.installments,
        key: `${dd.id}-${row.number}`,
      })),
    )
    .sort((a, b) => a.dueDate.localeCompare(b.dueDate));
  const months = [...new Set(rows.map((r) => r.dueDate.slice(0, 7)))];
  return (
    <div className="grid gap-4 lg:grid-cols-2 2xl:grid-cols-3">
      {months.map((month) => {
        const items = rows.filter((r) => r.dueDate.startsWith(month));
        return (
          <Panel
            key={month}
            title={formatBusinessDate(`${month}-01`).replace(/^1 /, '')}
            bodyClassName="p-0"
          >
            <ul className="divide-y divide-border">
              {items.map((r) => (
                <li
                  key={r.key}
                  className="flex items-center justify-between gap-3 px-5 py-2.5 text-sm"
                >
                  <span className="min-w-0">
                    <span className="block truncate font-medium">{r.merchant}</span>
                    <span className="text-xs text-muted-foreground">
                      Cuota {r.number} de {r.of} · vence {formatBusinessDate(r.dueDate)}
                    </span>
                  </span>
                  <span className="flex shrink-0 items-center gap-2">
                    <MoneyText value={r.total.amount} size="sm" />
                    <Badge variant={INSTALLMENT_STATUS[r.status].tone}>
                      {INSTALLMENT_STATUS[r.status].label}
                    </Badge>
                  </span>
                </li>
              ))}
            </ul>
          </Panel>
        );
      })}
    </div>
  );
}

function Statements({ statements }: { statements: readonly Statement[] }) {
  const [selected, setSelected] = useState<string | null>(statements[0]?.id ?? null);
  const statement = statements.find((s) => s.id === selected);
  if (statements.length === 0) {
    return (
      <EmptyState
        icon={<ReceiptTextIcon />}
        title="Sin extractos"
        description="El primer extracto se genera en la fecha de corte."
      />
    );
  }
  return (
    <div className="grid gap-6 lg:grid-cols-[18rem_minmax(0,1fr)]">
      <Panel bodyClassName="p-2">
        <ul className="flex flex-col gap-1">
          {statements.map((s) => (
            <li key={s.id}>
              <button
                type="button"
                onClick={() => {
                  setSelected(s.id);
                }}
                className={cn(
                  'w-full cursor-pointer rounded-lg px-3 py-2.5 text-left text-sm hover:bg-accent',
                  s.id === selected && 'bg-highlight-soft text-highlight-soft-foreground',
                )}
              >
                <span className="block font-semibold">
                  Corte {formatBusinessDate(s.cutoffDate)}
                </span>
                <span className="text-xs">
                  Pago mínimo <MoneyText value={s.minimumPayment.amount} size="sm" />
                </span>
              </button>
            </li>
          ))}
        </ul>
      </Panel>
      {statement && (
        <Panel
          title={`Extracto · corte ${formatBusinessDate(statement.cutoffDate)}`}
          description={`Periodo ${formatBusinessDate(statement.periodStart)} – ${formatBusinessDate(statement.cutoffDate)}`}
        >
          <div className="mb-5 grid gap-4 sm:grid-cols-3">
            <div className="rounded-xl bg-inverse p-4 text-inverse-foreground">
              <p className="text-xs text-inverse-muted-foreground">Pago mínimo</p>
              <MoneyText value={statement.minimumPayment.amount} size="lg" tone="inverse" />
            </div>
            <div className="rounded-xl border border-border p-4">
              <p className="text-xs text-muted-foreground">Fecha límite</p>
              <p className="font-display text-2xl font-semibold">
                {formatBusinessDate(statement.dueDate)}
              </p>
            </div>
            <div className="rounded-xl border border-border p-4">
              <p className="text-xs text-muted-foreground">Pago total</p>
              <MoneyText value={statement.payoffAmount.amount} size="lg" />
            </div>
          </div>
          <dl className="mb-5 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
            {(
              [
                ['Saldo anterior', statement.openingBalance.amount],
                ['Compras', statement.purchases.amount],
                ['Pagos', statement.payments.amount],
                ['Intereses', statement.interest.amount],
                ['Mora', statement.moratorium.amount],
                ['Seguro', statement.insurance.amount],
                ['Comisiones', statement.fees.amount],
              ] as const
            ).map(([label, value]) => (
              <div key={label}>
                <dt className="text-xs text-muted-foreground">{label}</dt>
                <dd>
                  <MoneyText value={value} size="sm" />
                </dd>
              </div>
            ))}
          </dl>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Concepto</TableHead>
                <TableHead className="text-right">Capital</TableHead>
                <TableHead className="text-right">Interés</TableHead>
                <TableHead className="text-right">Seguro</TableHead>
                <TableHead className="text-right">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {statement.lines.map((l, i) => (
                <TableRow key={i}>
                  <TableCell>
                    {l.description}
                    {l.excludedFromMinimum && (
                      <Badge variant="warning" className="ml-2">
                        En disputa
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <MoneyText value={l.principal.amount} size="sm" />
                  </TableCell>
                  <TableCell className="text-right">
                    <MoneyText value={l.interest.amount} size="sm" />
                  </TableCell>
                  <TableCell className="text-right">
                    <MoneyText value={l.insurance.amount} size="sm" />
                  </TableCell>
                  <TableCell className="text-right font-semibold">
                    <MoneyText value={l.total.amount} size="sm" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Panel>
      )}
    </div>
  );
}

function Payments({ detail }: { detail: AdminLineDetail }) {
  if (detail.payments.length === 0) {
    return (
      <EmptyState
        icon={<ReceiptTextIcon />}
        title="Sin pagos"
        description="Los pagos del cliente aparecerán con su aplicación por concepto."
      />
    );
  }
  return (
    <Panel bodyClassName="p-0">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Fecha</TableHead>
            <TableHead>Tipo</TableHead>
            <TableHead className="text-right">Valor</TableHead>
            <TableHead className="text-right">Mora</TableHead>
            <TableHead className="text-right">Interés</TableHead>
            <TableHead className="text-right">Seguro</TableHead>
            <TableHead className="text-right">Comisiones</TableHead>
            <TableHead className="text-right">Capital</TableHead>
            <TableHead>Estado</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {detail.payments.map((p) => (
            <TableRow key={p.id}>
              <TableCell>{formatDateTime(p.receivedAt)}</TableCell>
              <TableCell>{PAYMENT_TYPE[p.type]}</TableCell>
              <TableCell className="text-right font-semibold">
                <MoneyText value={p.amount.amount} size="sm" />
              </TableCell>
              <TableCell className="text-right">
                <MoneyText value={p.allocation?.interestLate.amount ?? '0'} size="sm" />
              </TableCell>
              <TableCell className="text-right">
                <MoneyText value={p.allocation?.interest.amount ?? '0'} size="sm" />
              </TableCell>
              <TableCell className="text-right">
                <MoneyText value={p.allocation?.insurance.amount ?? '0'} size="sm" />
              </TableCell>
              <TableCell className="text-right">
                <MoneyText value={p.allocation?.fees.amount ?? '0'} size="sm" />
              </TableCell>
              <TableCell className="text-right">
                <MoneyText value={p.allocation?.principal.amount ?? '0'} size="sm" />
              </TableCell>
              <TableCell>
                <Badge variant={p.status === 'APPLIED' ? 'success' : 'secondary'}>
                  {p.status === 'APPLIED' ? 'Aplicado' : p.status}
                </Badge>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Panel>
  );
}

const PAYMENT_TYPE = {
  MINIMUM: 'Pago mínimo',
  TOTAL: 'Pago total',
  CUSTOM: 'Otro valor',
  PREPAYMENT: 'Abono a capital',
} as const;

function Movements({ movements }: { movements: NonNullable<AdminLineDetail['movements']> }) {
  return (
    <Panel bodyClassName="p-0" description="Solo ADMIN y AUDITOR.">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Fecha de negocio</TableHead>
            <TableHead>Tipo</TableHead>
            <TableHead>Descripción</TableHead>
            <TableHead className="text-right">Valor</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {[...movements].reverse().map((m) => (
            <TableRow key={m.id}>
              <TableCell>{formatBusinessDate(m.businessDate)}</TableCell>
              <TableCell>
                <code className="font-mono text-xs">{m.type}</code>
              </TableCell>
              <TableCell>{m.description}</TableCell>
              <TableCell className="text-right">
                <MoneyText value={m.amount.amount} size="sm" sign="auto" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Panel>
  );
}
